import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles } from './styles/tokens.css.js';
import {
  createInitialState,
  canAdvance,
  prevStep,
  selectedEvent,
  isTicketedFlow,
  isRecurringFlow,
  isFixedDateEvent,
  skipsDatetime,
  fixedEventWhen,
  fixedEventDisplaySlot,
  paymentRequired,
  exitBookingMode,
  type BookingState,
} from './state/booking-store.js';
import { loadConfig, fetchAvailability, fetchOccurrences, goToNextStep, submitBooking, pollGhlPaymentStatus, fetchTicketQuote, invalidateTicketQuotes, verifyTicketPurchase, verifyDepositPayment, submitJoinNotify, normalizeCouponCode } from './state/actions.js';
import { resolveDepositForDate, resolveKidsDepositForDate, DEFAULT_PRIVACY_POLICY_URL } from '@yebo/shared';
import { apiClient } from './api/client.js';

// Import all child components (side-effects: custom element registration)
import './components/shared/sidebar-nav.js';
import './components/shared/error-banner.js';
import './components/shared/loading-spinner.js';
import './components/steps/step-service.js';
import './components/steps/step-datetime.js';
import './components/steps/step-contact.js';
import './components/steps/step-payment.js';
import './components/steps/step-confirm.js';

/**
 * <yebo-booking> — Amelia-style booking widget with sidebar + content layout.
 */
/** r93 X1 — one stable empty array, so a quote with no lines does not hand the
 *  steps a fresh `[]` (a changed property) on every host render. */
const NO_QUOTE_LINES: never[] = [];

@customElement('yebo-booking')
export class YeboBooking extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    css`
      :host {
        display: block;
        max-width: 640px;
        margin: 0 auto;
        font-family: var(--yebo-font);
        color: var(--yebo-text);
        border-radius: var(--yebo-radius);
        overflow: hidden;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
      }

      .widget-shell {
        display: flex;
        min-height: 420px;
        background: var(--yebo-bg);
      }

      /* Sidebar */
      .sidebar {
        flex: 0 0 220px;
        display: flex;
      }

      /* Content panel */
      .content {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: var(--yebo-space-lg) var(--yebo-space-xl);
        min-width: 0;
      }

      .content-body {
        flex: 1;
      }

      .error-wrap {
        margin-bottom: var(--yebo-space-md);
      }

      .loading-overlay {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        flex: 1;
      }

      /* Responsive: collapse sidebar on narrow screens. The sidebar itself
         swaps its vertical step list for a slim progress header at this same
         breakpoint (sidebar-nav.ts), and the host modal becomes a full-height
         sheet (book-now-button.ts) — keep all three queries at 540px. */
      @media (max-width: 540px) {
        .widget-shell {
          flex-direction: column;
        }
        .sidebar {
          flex: none;
        }
        .content {
          /* --yebo-space-xl side padding wastes ~64px on a 390px screen */
          padding: var(--yebo-space-md);
        }
      }
    `,
  ];

  // ── Public attributes ────────────────────────────────────────────────────

  @property({ type: String, attribute: 'location-id' })
  locationId = '';

  @property({ type: String, attribute: 'calendar-id' })
  calendarId = '';

  @property({ type: String, attribute: 'api-base' })
  apiUrl = '';

  @property({ type: String, attribute: 'theme-primary' })
  themePrimary = '';

  @property({ type: String })
  timezone = '';

  @property({ type: String, attribute: 'phone-number' })
  phoneNumber = '';

  @property({ type: String, attribute: 'privacy-policy-url' })
  privacyPolicyUrl = DEFAULT_PRIVACY_POLICY_URL;

  // Share-link deep link. A shared event link carries `event-id=evt_… | exp_…`
  // on the embed; the widget preselects that card on the service step once the
  // config (and thus the events list) has loaded. Without this @property Lit
  // silently drops the unknown attribute and the deep link is lost.
  @property({ type: String, attribute: 'event-id' })
  eventId = '';

  // `widgetBookingV2` flag (default OFF). Feature flags never travel in the
  // public config (they are private — #218/#267/#289), so the embed snippet
  // sets this boolean attribute explicitly when the tenant has the flag on.
  // OFF ⇒ the fast 2–3 step path is byte-for-byte unchanged (no recovery /
  // Notify / Add-to-Calendar / cross-sell surface).
  @property({ type: Boolean, attribute: 'widget-booking-v2' })
  widgetBookingV2 = false;

  // Optional sibling-venue cross-sell (R10.9). Set via the embed when the tenant
  // has a sibling location; rendered only in the empty state under the V2 flag.
  @property({ type: String, attribute: 'sibling-venue-name' })
  siblingVenueName = '';

  @property({ type: String, attribute: 'sibling-venue-url' })
  siblingVenueUrl = '';

  // ── Internal reactive state ──────────────────────────────────────────────

  @state() private _state: BookingState = createInitialState();

  private _updater = (patch: Partial<BookingState>) => {
    this._state = { ...this._state, ...patch };
  };

  // Guard so the share-link preselect runs at most once for the widget's life
  // (config lands asynchronously; `updated()` fires on every reactive change).
  private _eventPreselectDone = false;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  updated(changed: Map<string | number | symbol, unknown>): void {
    super.updated?.(changed);
    this._maybePreselectEvent();
    this._applyThemeVars();
  }

  /**
   * Map the fetched widget-config theme onto the host CSS custom properties the
   * token layer already consumes (styles/tokens.css.ts). Mirrors the ordering
   * widget's `_applyThemeVars`. PRECEDENCE: an explicit `theme-primary` attribute
   * on the embed always wins over the fetched `primaryColor` (connectedCallback
   * has already applied it), so we never overwrite it here. Font/radius have no
   * attribute counterpart and apply directly. A missing field is left unset so
   * the tokens.css.ts default takes over. No-op until the theme lands.
   */
  private _applyThemeVars(): void {
    const t = this._state.theme;
    // `this.style` is absent in the node test env; guard so it degrades safely.
    if (!t || !this.style) return;
    if (t.primaryColor && !this.themePrimary) {
      this.style.setProperty('--booking-primary-color', t.primaryColor);
    }
    if (t.fontFamily) {
      this.style.setProperty('--booking-font-family', t.fontFamily);
    }
    if (t.borderRadius) {
      this.style.setProperty('--booking-border-radius', t.borderRadius);
    }
    if (t.backgroundColor) {
      this.style.setProperty('--booking-bg-color', t.backgroundColor);
    }
    if (t.sidebarBackgroundColor) {
      this.style.setProperty('--booking-sidebar-bg', t.sidebarBackgroundColor);
    }
    if (t.sidebarTextColor) {
      this.style.setProperty('--booking-sidebar-text', t.sidebarTextColor);
    }
    if (t.textColor) {
      this.style.setProperty('--booking-text-color', t.textColor);
    }
    if (t.textMutedColor) {
      this.style.setProperty('--booking-text-muted', t.textMutedColor);
    }
    if (t.borderColor) {
      this.style.setProperty('--booking-border-color', t.borderColor);
    }
    // #653 — event/ticket surface colours. Each is only SET when the venue has
    // chosen one; leaving the property unset is what lets tokens.css.ts fall
    // through to the colour that surface used before, so an unthemed tenant is
    // byte-identical.
    if (t.eventHeadingColor) {
      this.style.setProperty('--booking-event-heading', t.eventHeadingColor);
    }
    if (t.eventDescriptionColor) {
      this.style.setProperty('--booking-event-description', t.eventDescriptionColor);
    }
    if (t.eventPriceColor) {
      this.style.setProperty('--booking-event-price', t.eventPriceColor);
    }
  }

  // Apply the share-link `event-id` deep link exactly once, after the config has
  // loaded and the events list is populated. We reuse `_onEventSelect` verbatim
  // so the selection, party-size clamping, quote fetch (one-off) and occurrence
  // pre-warm (recurring) are byte-identical to a manual tap. Preselect + highlight
  // only — no forced auto-advance (a recurring series can't advance without an
  // occurrence; a one-off gets its quote from the handler and the guest continues).
  private _maybePreselectEvent(): void {
    if (this._eventPreselectDone) return;
    if (!this.eventId) return;
    const s = this._state;
    // Wait for config: the events list is empty until loadConfig resolves.
    if (!s.configLoaded || !s.events.length) return;

    // From here we've seen a populated events list — never re-attempt, whether or
    // not the id matches (an unknown id is silently ignored, widget stays usable).
    this._eventPreselectDone = true;

    const ev = s.events.find((x) => x.id === this.eventId);
    if (!ev) return;

    // MVP-4 — a deep link is an explicit "events" intent: put the service step in
    // the events path so the two-card chooser is skipped and the preselected card
    // shows highlighted immediately.
    this._updater({ bookingMode: 'events' });

    this._onEventSelect(
      // #947 — carry the event's date exactly as a manual tap does (step-service
      // dispatches `date`), so a date-only event opened from a share link opens
      // Date & Time ON its date. A recurring series ignores it (#130).
      new CustomEvent('event-select', { detail: { eventId: ev.id, name: ev.name, date: ev.date } }),
    );
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (this.themePrimary) {
      this.style.setProperty('--booking-primary-color', this.themePrimary);
    }

    if (this.apiUrl) {
      Object.assign(apiClient, { baseUrl: this.apiUrl });
    }

    this._updater({
      locationId: this.locationId,
      calendarId: this.calendarId,
      timezone: this.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      widgetBookingV2: this.widgetBookingV2,
      siblingVenue: this.siblingVenueName && this.siblingVenueUrl
        ? { name: this.siblingVenueName, url: this.siblingVenueUrl }
        : null,
    });

    if (this.locationId) {
      loadConfig(this._state, this._updater);
    }
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  private _onPartySizeChange(e: CustomEvent<{ partySize: number }>) {
    const next = e.detail.partySize;
    const sizeChanged = next !== this._state.partySize;
    this._updater({ partySize: next });
    // Re-quote a ticketed event when the party size changes (server-authoritative).
    if (isTicketedFlow(this._state)) {
      queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
    }
    // #521 — since availability became party-aware, party size is a real input
    // to the answer: a seven may be refused where a two is offered. Previously
    // fetchAvailability fired ONLY on date selection, so changing the size left
    // the PREVIOUS party's times on screen. The damaging direction is downward —
    // a guest who looked at 7, saw nothing, and dropped back to 2 kept seeing an
    // empty list and gave up, losing a booking the venue could have taken.
    // Slots + any chosen slot are cleared synchronously so the stale list is
    // never selectable while the new answer is in flight.
    if (sizeChanged && this._state.selectedDate) {
      this._updater({ availableSlots: [], selectedSlot: null, selectedSlotEnd: null });
      queueMicrotask(() => fetchAvailability(this._state, this._updater));
    }
  }

  private _onKidsCountChange(e: CustomEvent<{ kidsCount: number }>) {
    this._updater({ kidsCount: e.detail.kidsCount });
    // Re-quote a ticketed event when the kids/adults split changes — kids may
    // price differently (server-authoritative).
    if (isTicketedFlow(this._state)) {
      queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
    }
  }

  // MVP-4 — the guest picked a path on the two-card service chooser.
  private _onBookingModeSelect(e: CustomEvent<{ mode: 'table' | 'events' }>) {
    this._updater({ bookingMode: e.detail.mode });
  }

  // MVP-4 — "‹ Back" from either path returns to the chooser. Leaving the events
  // path also clears any event selection (via the shared exitBookingMode patch).
  private _onBookingModeBack() {
    this._updater(exitBookingMode(this._state));
  }

  // #159: selecting a special event presets (and constrains) the date to the
  // event's date; deselecting clears it. The eventId rides along to booking.
  // When the event sells prepaid tickets, also seed the tier (if any) and fetch
  // an authoritative quote so the displayed price + availability are live.
  private _onEventSelect(e: CustomEvent<{ eventId: string | null; name?: string; date?: string }>) {
    const { eventId, name, date } = e.detail;
    // r93 G3 N1 — a quote still in flight for the previous event must not land
    // on this one (its total, code, deal and tier label would all be wrong).
    if (eventId !== this._state.selectedEventId) invalidateTicketQuotes();
    if (!eventId) {
      // #947 — a fixed-date event wrote its own slot on the skip; that slot must
      // not survive into the table path (or another event) once deselected.
      const clearFixedSlot = isFixedDateEvent(this._state)
        ? { selectedSlot: null, selectedSlotEnd: null }
        : {};
      this._updater({
        ...clearFixedSlot,
        selectedEventId: null,
        selectedEventName: null,
        selectedTierId: null,
        ticketQuote: null,
        ticketAvailability: null,
        ticketQuoteError: null,
        couponCode: '',
        couponApplied: null,
        couponError: null,
        couponApplying: false,
        autoDeal: null,
        // r93 — a deselect empties the #652 basket too, so its lines can't be
        // quoted against the next event's tiers.
        ticketLines: [],
        // #130 — clear any recurring-occurrence state on deselect.
        experienceOccurrences: [],
        selectedOccurrenceId: null,
        selectedOccurrenceDate: null,
      });
      return;
    }

    // Default the tier to the first option for ticketed events with tiers, and
    // clamp party size into the event's min/max before quoting.
    const ev = this._state.events.find((x) => x.id === eventId);
    const ticketing = ev?.ticketing ?? null;
    const firstTierId = ticketing?.tiers?.[0]?.id ?? null;
    let partySize = this._state.partySize;
    if (ticketing) {
      const min = Math.max(1, ticketing.minPartySize ?? 1);
      let max = ticketing.maxPartySize ?? Number.MAX_SAFE_INTEGER;
      if (typeof ticketing.coversRemaining === 'number') max = Math.min(max, ticketing.coversRemaining);
      partySize = Math.min(Math.max(partySize, min), Math.max(min, max));
    }

    // #130 — a recurring series has NO fixed date: it does not preset
    // `selectedDate`, and its occurrence (and quote) are chosen on the next step.
    const recurring = !!ev?.recurring;

    this._updater({
      // r93 — switching to a DIFFERENT event starts an empty basket; lines from
      // event A carry A's tier ids and must never be quoted against event B.
      ...(eventId !== this._state.selectedEventId ? { ticketLines: [] } : {}),
      selectedEventId: eventId,
      selectedEventName: name ?? null,
      selectedTierId: firstTierId,
      ticketQuote: null,
      ticketAvailability: null,
      ticketQuoteError: null,
      // #816 — a promo code belongs to the event it was quoted against.
      couponCode: '',
      couponApplied: null,
      couponError: null,
      couponApplying: false,
      autoDeal: null,
      partySize,
      // #130 — reset any prior occurrence selection when (re)selecting a card.
      experienceOccurrences: [],
      selectedOccurrenceId: null,
      selectedOccurrenceDate: null,
      ...(recurring
        ? { selectedDate: null, selectedSlot: null, selectedSlotEnd: null, availableSlots: [] }
        : date
          ? { selectedDate: date, selectedSlot: null, selectedSlotEnd: null, availableSlots: [] }
          : {}),
    });

    // #130 — pre-warm the lazy occurrence list for a recurring series (its
    // per-date covers are independent of party size). A one-off ticketed event
    // still fetches its authoritative quote here, unchanged.
    if (recurring) {
      queueMicrotask(() => fetchOccurrences(this._state, this._updater));
    } else if (ticketing) {
      queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
    }
  }

  // Ticketed event: guest picked a price tier — re-quote to update the total.
  /**
   * #651 — fetch a fresh presigned URL and hand it to the browser.
   *
   * Deliberately NOT a link: the widget token rides a header, so only a fetch
   * can reach the endpoint, and the URL it returns lives ~2 minutes. Opening
   * it as a top-level navigation (rather than fetching the bytes into a blob)
   * keeps the S3 leg out of CORS — the bucket only allows the venue origins we
   * know about, and a widget embedded on a third-party site is not one.
   */
  private async _onMenuPdfRequest(e: CustomEvent<{ eventId: string }>) {
    const eventId = e.detail?.eventId;
    if (!eventId || this._state.menuPdfBusy) return;
    this._updater({ menuPdfBusy: true, menuPdfError: null });
    try {
      const url = await apiClient.getEventMenuPdfUrl(eventId);
      window.open(url, '_blank', 'noopener');
      this._updater({ menuPdfBusy: false });
    } catch {
      // The reason is never useful to a guest and a 404 here means the venue
      // removed the file — say the true thing without the status code.
      this._updater({
        menuPdfBusy: false,
        menuPdfError: 'That download is not available right now.',
      });
    }
  }

  /**
   * #652 — the guest changed the basket.
   *
   * `partySize` and `kidsCount` are written from the lines rather than tracked
   * independently, so the number the widget shows and the number the server
   * derives from Σ qty cannot disagree. `selectedTierId` is kept in step for a
   * single-tier basket only — that is what the legacy scalar path reads, and a
   * mixed basket genuinely has no single tier.
   */
  private _onTicketLinesChange(
    e: CustomEvent<{ lines: Array<{ tierId: string; qty: number; kidsQty: number }>; partySize: number; kidsCount: number }>,
  ) {
    const { lines, partySize, kidsCount } = e.detail;
    this._updater({
      ticketLines: lines,
      // G3 payments M6 — an EMPTY basket must not write `partySize: 0` into
      // shared state. `exitBookingMode` clears `ticketLines` but not
      // `partySize`, so building a basket, emptying it, and pressing "Back"
      // handed the table-booking flow a party of zero. Continue is already
      // gated on a non-empty basket, so holding the last real value costs
      // nothing and keeps the other path sane.
      ...(lines.length > 0 ? { partySize, kidsCount } : {}),
      selectedTierId: lines.length === 1 ? lines[0].tierId : null,
    });
    queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
  }

  // ── #816 — the promo code ──────────────────────────────────────────────────
  //
  // The widget owns the CODE, the server owns the MONEY. Applying is just a
  // re-quote with `couponCode`; the discount, the new total and the refusal
  // sentence all arrive from that round-trip. Nothing is computed here.

  private _onCouponApply(e: CustomEvent<{ code: string }>) {
    const code = normalizeCouponCode(e.detail?.code);
    this._updater({ couponCode: code, couponError: null });
    queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
  }

  /** Emptying the field drops the discount and re-quotes at FULL price. */
  private _onCouponClear() {
    this._updater({ couponCode: '', couponApplied: null, couponError: null });
    queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
  }

  private _onTierSelect(e: CustomEvent<{ tierId: string }>) {
    this._updater({ selectedTierId: e.detail.tierId });
    queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
  }

  private _onDateSelected(e: CustomEvent<{ date: string }>) {
    this._updater({
      selectedDate: e.detail.date,
      selectedSlot: null,
      selectedSlotEnd: null,
      availableSlots: [],
    });
    queueMicrotask(() => fetchAvailability(this._state, this._updater));
  }

  private _onSlotSelected(e: CustomEvent<{ start: string; end: string }>) {
    this._updater({
      selectedSlot: e.detail.start,
      selectedSlotEnd: e.detail.end,
    });
  }

  // #130 — guest picked a recurring occurrence. Store the OPAQUE occId verbatim
  // (sent unchanged to quote/checkout/book) + its date for display, then fetch an
  // authoritative quote for this specific sitting. The occId is never parsed here.
  private _onOccurrenceSelected(e: CustomEvent<{ occId: string; date: string }>) {
    const { occId, date } = e.detail;
    this._updater({
      selectedOccurrenceId: occId,
      selectedOccurrenceDate: date,
      // Mirror the chosen date onto `selectedDate` so the confirm/summary views
      // (which read `ticketDate || selectedDate`) show the right sitting.
      selectedDate: date,
      ticketQuote: null,
      ticketAvailability: null,
      ticketQuoteError: null,
      // #816 — a promo code belongs to the event it was quoted against.
      couponCode: '',
      couponApplied: null,
      couponError: null,
      couponApplying: false,
      autoDeal: null,
    });
    queueMicrotask(() => fetchTicketQuote(this._state, this._updater));
  }

  // ── R10.4 empty-state recovery handlers ────────────────────────────────────

  // Guest picked an alternate same-day time (possibly in another online
  // section). Treat it exactly like a normal slot selection.
  private _onAlternateTimeSelected(e: CustomEvent<{ start: string; end: string }>) {
    this._updater({
      selectedSlot: e.detail.start,
      selectedSlotEnd: e.detail.end,
    });
  }

  // "Try another date" — jump the calendar/availability to the server-suggested
  // next available date and fetch its slots.
  private _onTryAnotherDate(e: CustomEvent<{ date: string }>) {
    const date = e.detail.date;
    if (!date) return;
    this._updater({
      selectedDate: date,
      selectedSlot: null,
      selectedSlotEnd: null,
      availableSlots: [],
    });
    queueMicrotask(() => fetchAvailability(this._state, this._updater));
  }

  // R10.6 — Join Notify from the empty state. Requires at least a name + email;
  // the empty-state form collects them inline.
  private _onJoinNotify(e: CustomEvent<{ firstName: string; lastName?: string; email: string; phone?: string }>) {
    const { firstName, lastName, email, phone } = e.detail;
    this._updater({
      firstName: firstName ?? this._state.firstName,
      lastName: lastName ?? this._state.lastName,
      email: email ?? this._state.email,
      phone: phone ?? this._state.phone,
    });
    queueMicrotask(() => submitJoinNotify(this._state, this._updater));
  }

  private _onFieldChange(e: CustomEvent<{ field: string; value: string | boolean }>) {
    const { field, value } = e.detail;
    if (field === 'highChairNeeded') {
      this._updater({ highChairNeeded: value === true || value === 'true' });
      return;
    }
    if (field === 'consentGiven') {
      this._updater({ consentGiven: value === true || value === 'true' });
      return;
    }
    if (field in this._state) {
      this._updater({ [field]: value } as Partial<BookingState>);
    }
  }

  private async _onStepContinue() {
    if (!canAdvance(this._state)) return;

    // #480 — the fabricated `POST /deposit/charge` with
    // `reservationId: pending_${Date.now()}`, and the `catch {}` that walked a
    // guest whose deposit failed through to a FREE confirmed booking, used to
    // live here. Both are DELETED (design §5.2, ruling R4). Deciding whether a
    // deposit is owed is `/deposit/checkout`'s job and it is reached through
    // the step machine, where a failure is visible and stops the booking.
    await goToNextStep(this._state, this._updater);
  }

  private _onStepBack() {
    const prev = prevStep(this._state);
    if (prev) this._updater({ currentStep: prev, error: null });
  }

  private _onPaymentSucceeded(e: CustomEvent<{ paymentIntentId: string }>) {
    this._updater({
      paymentIntentId: e.detail.paymentIntentId,
      paymentStatus: 'succeeded',
    });
    // Ticketed purchase: the seat is already held — verify the charge cleared
    // instead of running the normal book + deposit submit.
    if (isTicketedFlow(this._state)) {
      queueMicrotask(() => verifyTicketPurchase(this._state, this._updater));
      return;
    }
    // #480 — a deposit checkout also already holds the seat (status 'pending').
    // Settle + confirm it; never submit a second booking.
    if (this._state.depositReservationId) {
      queueMicrotask(() => verifyDepositPayment(this._state, this._updater));
      return;
    }
    queueMicrotask(() => submitBooking(this._state, this._updater));
  }

  private _onPaymentFailed(e: CustomEvent<{ message: string }>) {
    this._updater({
      paymentStatus: 'failed',
      error: e.detail.message || 'Payment failed. Please try again.',
    });
  }

  private async _onPollPaymentStatus() {
    // Ticketed redirect (authorizationUrl) flow: poll the ticket verify endpoint
    // directly — there is no GHL order to poll. verify() returns success only
    // once the gateway confirms payment, then advances to confirmation. Quiet
    // mode keeps not-yet-paid polls from flashing an error.
    if (isTicketedFlow(this._state)) {
      await verifyTicketPurchase(this._state, this._updater, { quiet: true });
      return;
    }
    // #480 — a deposit booking on a hosted gateway (PayFast/Paystack/Square)
    // polls /deposit/verify. This is the PRIMARY resume path: the hosted page
    // is opened with `noopener`, so this tab stays alive and keeps polling —
    // the static return page (§8.3) is only the fallback for a lost tab.
    if (this._state.depositReservationId && this._state.depositDate) {
      await verifyDepositPayment(this._state, this._updater, { quiet: true });
      return;
    }
    const paid = await pollGhlPaymentStatus(this._state, this._updater);
    if (paid) {
      queueMicrotask(() => submitBooking(this._state, this._updater));
    }
  }

  /**
   * #953 — the confirmed order was a 100%-comped ticket purchase: a ticketed
   * flow that settled (`paymentStatus: 'succeeded'`, set by the checkout's
   * comped branch on the server's `paid`/`waived` answer) at a server amount of
   * 0, with a code the server accepted. Drives honest "Complimentary" copy on
   * the confirmation instead of a silent or R0.00 "Total paid".
   */
  private _isCompedConfirm(): boolean {
    const s = this._state;
    return isTicketedFlow(s)
      && s.paymentStatus === 'succeeded'
      && (s.paymentAmount ?? 0) <= 0
      // #954 — a 100% AUTO deal comps exactly like a typed code.
      && (!!s.couponApplied?.code || s.autoDeal?.applied === true);
  }

  private _onDismissError() {
    this._updater({ error: null });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  private _renderStep() {
    const s = this._state;

    switch (s.currentStep) {
      case 'service':
        return html`
          <yebo-step-service
            party-size=${s.partySize}
            kids-count=${s.kidsCount}
            max-party=${s.maxOnlinePartySize >= 1 ? s.maxOnlinePartySize : 20}
            contact-phone=${s.contactPhone ?? ''}
            contact-email=${s.contactEmail ?? ''}
            booking-mode=${s.bookingMode ?? ''}
            .events=${s.events}
            selected-event-id=${s.selectedEventId ?? ''}
            selected-tier-id=${s.selectedTierId ?? ''}
            ticket-total-cents=${s.ticketQuote?.totalCents ?? 0}
            ticket-unit-cents=${s.ticketQuote?.unitPriceCents ?? 0}
            .ticketQuoteLines=${s.ticketQuote?.lines ?? NO_QUOTE_LINES}
            ticket-kids-unit-cents=${s.ticketQuote?.kidsUnitPriceCents ?? 0}
            ticket-currency=${s.ticketQuote?.currency ?? ''}
            ticket-remaining=${s.ticketAvailability?.remaining ?? -1}
            ?ticket-sold-out=${s.ticketAvailability?.soldOut ?? false}
            ticket-error=${s.ticketQuoteError ?? ''}
            ticket-subtotal-cents=${s.ticketQuote?.subtotalCents ?? 0}
            ticket-discount-cents=${s.ticketQuote?.discountCents ?? 0}
            coupon-code=${s.couponCode}
            coupon-applied-code=${s.couponApplied?.code ?? ''}
            coupon-percent-off=${s.couponApplied?.percentOff ?? 0}
            coupon-tickets-remaining=${s.couponApplied?.ticketsRemaining ?? -1}
            coupon-error=${s.couponError ?? ''}
            ?coupon-applying=${s.couponApplying}
            coupon-applied-label=${s.couponApplied?.label ?? ''}
            ?deal-applied=${s.autoDeal?.applied ?? false}
            deal-label=${s.autoDeal?.label ?? ''}
            deal-reason=${s.autoDeal?.reason ?? ''}
            deal-message=${s.autoDeal?.message ?? ''}
            @coupon-apply=${this._onCouponApply}
            @coupon-clear=${this._onCouponClear}
            @party-size-change=${this._onPartySizeChange}
            @kids-count-change=${this._onKidsCountChange}
            @booking-mode-select=${this._onBookingModeSelect}
            @booking-mode-back=${this._onBookingModeBack}
            @event-select=${this._onEventSelect}
            .ticketLines=${s.ticketLines}
            ?menu-pdf-busy=${s.menuPdfBusy}
            menu-pdf-error=${s.menuPdfError ?? ''}
            @tier-select=${this._onTierSelect}
            @menu-pdf-request=${this._onMenuPdfRequest}
            @ticket-lines-change=${this._onTicketLinesChange}
            @step-continue=${this._onStepContinue}
          ></yebo-step-service>
        `;
      case 'datetime':
        return html`
          <yebo-step-datetime
            timezone=${s.timezone}
            selected-date=${s.selectedDate ?? ''}
            selected-slot=${s.selectedSlot ?? ''}
            .slots=${s.availableSlots}
            .schedules=${s.workingHoursSchedules}
            ?loading=${s.loading}
            ?recurring=${isRecurringFlow(s)}
            .occurrences=${s.experienceOccurrences}
            ?occurrences-loading=${s.occurrencesLoading}
            selected-occurrence-id=${s.selectedOccurrenceId ?? ''}
            party-size=${s.partySize}
            kids-count=${s.kidsCount}
            ticket-total-cents=${s.ticketQuote?.totalCents ?? 0}
            ticket-unit-cents=${s.ticketQuote?.unitPriceCents ?? 0}
            .ticketQuoteLines=${s.ticketQuote?.lines ?? NO_QUOTE_LINES}
            ticket-kids-unit-cents=${s.ticketQuote?.kidsUnitPriceCents ?? 0}
            ticket-currency=${s.ticketQuote?.currency ?? ''}
            ticket-error=${s.ticketQuoteError ?? ''}
            ticket-subtotal-cents=${s.ticketQuote?.subtotalCents ?? 0}
            ticket-discount-cents=${s.ticketQuote?.discountCents ?? 0}
            coupon-code=${s.couponCode}
            coupon-applied-code=${s.couponApplied?.code ?? ''}
            coupon-percent-off=${s.couponApplied?.percentOff ?? 0}
            coupon-tickets-remaining=${s.couponApplied?.ticketsRemaining ?? -1}
            coupon-error=${s.couponError ?? ''}
            ?coupon-applying=${s.couponApplying}
            coupon-applied-label=${s.couponApplied?.label ?? ''}
            ?deal-applied=${s.autoDeal?.applied ?? false}
            deal-label=${s.autoDeal?.label ?? ''}
            deal-reason=${s.autoDeal?.reason ?? ''}
            deal-message=${s.autoDeal?.message ?? ''}
            @coupon-apply=${this._onCouponApply}
            @coupon-clear=${this._onCouponClear}
            ?recovery-enabled=${s.widgetBookingV2}
            availability-reason=${s.availabilityReason ?? ''}
            venue-phone=${this.phoneNumber || s.contactPhone || ''}
            venue-email=${s.contactEmail ?? ''}
            .alternateTimes=${s.alternateTimes}
            .alternateSections=${s.alternateSections}
            next-available-date=${s.nextAvailableDate ?? ''}
            .siblingVenue=${s.siblingVenue}
            first-name=${s.firstName}
            last-name=${s.lastName}
            email=${s.email}
            phone=${s.phone}
            ?notify-submitting=${s.notifySubmitting}
            ?notify-submitted=${s.notifySubmitted}
            notify-error=${s.notifyError ?? ''}
            @date-selected=${this._onDateSelected}
            @slot-selected=${this._onSlotSelected}
            @occurrence-selected=${this._onOccurrenceSelected}
            @alternate-time-selected=${this._onAlternateTimeSelected}
            @try-another-date=${this._onTryAnotherDate}
            @join-notify=${this._onJoinNotify}
            @step-back=${this._onStepBack}
            @step-continue=${this._onStepContinue}
          ></yebo-step-datetime>
        `;
      case 'contact':
        return html`
          <yebo-step-contact
            first-name=${s.firstName}
            last-name=${s.lastName}
            email=${s.email}
            phone=${s.phone}
            notes=${s.notes}
            kids-count=${s.kidsCount}
            dietary-restrictions=${s.dietaryRestrictions}
            ?high-chair-needed=${s.highChairNeeded}
            dietary-details=${s.dietaryDetails}
            ?consent-given=${s.consentGiven}
            privacy-policy-url=${this.privacyPolicyUrl}
            ?progressive-fields=${s.widgetBookingV2}
            dietary-state=${s.fieldStates?.dietary ?? 'optional'}
            seating-state=${s.fieldStates?.seatingPreference ?? 'optional'}
            occasion-state=${s.fieldStates?.occasion ?? 'optional'}
            accessibility-needs=${s.accessibilityNeeds}
            seating-preference=${s.seatingPreference}
            occasion=${s.occasion}
            booking-when=${fixedEventWhen(s)}
            ?loading=${s.loading}
            @field-change=${this._onFieldChange}
            @step-back=${this._onStepBack}
            @step-continue=${this._onStepContinue}
          ></yebo-step-contact>
        `;
      case 'payment': {
        // Compute display amount: split adults vs kids deposit, or legacy flat amount
        let displayAmount = s.paymentAmount;
        let displayCurrency = s.paymentCurrency;
        let perPersonAmount = 0;
        let perKidAmount = 0;
        const kidsCount = s.kidsCount || 0;
        if (s.depositConfig && s.selectedDate) {
          perPersonAmount = resolveDepositForDate(s.depositConfig, s.selectedDate);
          perKidAmount = resolveKidsDepositForDate(s.depositConfig, s.selectedDate);
          const adults = s.partySize - kidsCount;
          displayAmount = (perPersonAmount * adults) + (perKidAmount * kidsCount);
          displayCurrency = s.depositConfig.currency;
        }
        return html`
          <yebo-step-payment
            payment-provider=${s.paymentProvider}
            payment-amount=${displayAmount}
            payment-currency=${displayCurrency}
            client-secret=${s.paymentClientSecret ?? ''}
            stripe-key=${s.stripePublicKey}
            authorization-url=${s.depositAuthorizationUrl}
            deposit-expires-at=${s.depositExpiresAt ?? ''}
            policy-note=${s.depositPolicyNote ?? ''}
            ghl-payment-url=${s.ghlPaymentUrl ?? ''}
            ghl-order-id=${s.ghlOrderId ?? ''}
            party-size=${s.partySize}
            kids-count=${kidsCount}
            per-person-amount=${perPersonAmount}
            per-kid-amount=${perKidAmount}
            deposit-type=${s.depositType || ''}
            ?loading=${s.loading}
            @step-back=${this._onStepBack}
            @payment-succeeded=${this._onPaymentSucceeded}
            @payment-failed=${this._onPaymentFailed}
            @poll-payment-status=${this._onPollPaymentStatus}
          ><!-- #848: forward the host's light-DOM Stripe mount node (slot="stripe-payment"
               on <yebo-booking>) into the payment step, so the card form renders in place
               while its iframe lives outside every shadow root. -->
            <slot name="stripe-payment" slot="stripe-payment"></slot>
          </yebo-step-payment>
        `;
      }
      case 'confirm': {
        const ticketed = isTicketedFlow(s);
        const ev = selectedEvent(s);
        // For a ticketed purchase the "slot" is the event date + start time.
        const ticketSlot = ticketed && (s.ticketDate || s.selectedDate)
          ? `${s.ticketDate || s.selectedDate}T${ev?.start || '00:00'}:00`
          : '';
        // End instant for the calendar event (R10.7): the event's end time for
        // a ticketed booking, otherwise the selected slot end.
        const ticketSlotEnd = ticketed && (s.ticketDate || s.selectedDate) && ev?.end
          ? `${s.ticketDate || s.selectedDate}T${ev.end}:00`
          : '';
        // #947 — a fixed-date non-ticketed booking stores a NAIVE venue wall
        // clock (the wire value); the summary formats an instant, so it gets the
        // venue-anchored form. Every other flow binds exactly what it did before.
        const fixedShown = ticketed ? null : fixedEventDisplaySlot(s);
        return html`
          <yebo-step-confirm
            appointment-id=${s.appointmentId ?? ''}
            confirmation-number=${s.confirmationNumber ?? ''}
            selected-slot=${ticketed ? ticketSlot : (fixedShown?.start ?? s.selectedSlot ?? '')}
            selected-slot-end=${ticketed ? ticketSlotEnd : (fixedShown?.end ?? s.selectedSlotEnd ?? '')}
            timezone=${s.timezone}
            party-size=${s.partySize}
            kids-count=${s.kidsCount}
            name=${`${s.firstName} ${s.lastName}`.trim()}
            business-name=${s.businessName}
            ?recovery-enabled=${s.widgetBookingV2}
            manage-booking-url=${s.manageBookingUrl}
            ?ticketed=${ticketed}
            event-name=${s.selectedEventName ?? ''}
            tier-label=${s.ticketQuote?.tierLabel ?? ''}
            amount-cents=${ticketed ? s.paymentAmount : 0}
            currency=${ticketed ? s.paymentCurrency : ''}
            ?comped=${this._isCompedConfirm()}
            discount-label=${ticketed ? (s.ticketDiscountLine?.label ?? '') : ''}
            discount-cents=${ticketed ? (s.ticketDiscountLine?.discountCents ?? 0) : 0}
            ?show-time=${ticketed ? !!ev?.start : true}
          ></yebo-step-confirm>
        `;
      }
      default:
        return nothing;
    }
  }

  render() {
    const s = this._state;

    if (!this.locationId) {
      return html`<p style="color: var(--yebo-error)">Missing required attribute: location-id</p>`;
    }

    if (!s.configLoaded && s.loading) {
      return html`
        <div class="widget-shell">
          <div class="loading-overlay">
            <yebo-loading-spinner></yebo-loading-spinner>
          </div>
        </div>
      `;
    }

    return html`
      <div class="widget-shell">
        <div class="sidebar">
          <!-- #953 G3: a SERVER-settled comp never visited payment; don't draw it
               as a ticked step. Keyed on _isCompedConfirm(), never the quote. -->
          <yebo-sidebar-nav
            current=${s.currentStep}
            ?hide-payment=${!paymentRequired(s) || this._isCompedConfirm()}
            ?hide-datetime=${skipsDatetime(s)}
            phone-number=${this.phoneNumber}
          ></yebo-sidebar-nav>
        </div>

        <div class="content">
          ${s.error ? html`
            <div class="error-wrap">
              <yebo-error-banner
                message=${s.error}
                @dismiss=${this._onDismissError}
              ></yebo-error-banner>
            </div>
          ` : nothing}

          <div class="content-body">
            ${this._renderStep()}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-booking': YeboBooking;
  }
}
